import { AvForm } from 'availity-reactstrap-validation'
import RenderFields from '../../../../CMSComponent/Forms/renderFields'
import { useEffect, useState } from 'react'
import { Input } from 'reactstrap'
import { primary_color } from '../../../../Helper/uiHelper'
import { request } from '../../../../Redux/Sagas/requests/api'
import { useDispatch } from 'react-redux'
import { setNotification } from '../../../../Redux/Reducers/notificationHandling'

const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '')

const formatApiMessage = (message) => {
  if (!message) return 'Failed to update code'
  if (typeof message === 'string') return message
  if (typeof message === 'object') return Object.values(message).flat().join(' ')
  return 'Failed to update code'
}

const validateLockerCode = (code) => {
  const digits = digitsOnly(code)
  if (!digits) {
    return 'Code is required and must be exactly 4 digits.'
  }
  if (digits.length !== 4) {
    return 'Code must be exactly 4 digits. Longer or shorter codes cannot be saved.'
  }
  if (!/^\d{4}$/.test(digits)) {
    return 'Code must be exactly 4 numeric digits.'
  }
  return null
}

/**
 * Manual / Block Codes locker edit (Blocks → Codes).
 * Validates code with a clear red field error and keeps the form open on failure
 * so the user can correct and save without re-opening edit.
 */
const AddEditForm = ({ form, onCloseForm, defaultValue }) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [formFieldValue, setFormFieldValue] = useState()
  const [code, setCode] = useState(digitsOnly(defaultValue?.code).slice(0, 8))
  const [codeError, setCodeError] = useState(null)

  const formWithoutCode = {
    ...form,
    component_call_fields: (form?.component_call_fields || []).filter(
      (field) => field?.field_id !== 'code' && field?.name !== 'code'
    ),
  }

  useEffect(() => {
    setCode(digitsOnly(defaultValue?.code).slice(0, 8))
    setCodeError(null)
  }, [defaultValue?.id, defaultValue?.code])

  const onCodeChange = (raw) => {
    // Allow typing while editing so user can correct mistakes; cap at 8 to avoid runaway input.
    const next = digitsOnly(raw).slice(0, 8)
    setCode(next)
    if (codeError) {
      setCodeError(validateLockerCode(next))
    }
  }

  const onUpdateLocker = (values) => {
    const err = validateLockerCode(code)
    if (err) {
      setCodeError(err)
      setLoading(false)
      dispatch(setNotification({ type: 'error', message: err }))
      return
    }

    setCodeError(null)
    setLoading(true)
    request('insert-codes', 'POST', {
      id: defaultValue?.id,
      ...values,
      code: digitsOnly(code),
    }).then((res) => {
      setLoading(false)
      if (res?.status === 200 || res?.status === 202 || res?.status === 201) {
        dispatch(setNotification({ type: 'success', message: 'Code updated successfully!' }))
        onCloseForm()
        return
      }
      const message = formatApiMessage(res?.data?.message)
      setCodeError(message)
      dispatch(setNotification({ type: 'error', message }))
      // Keep form open so the user can fix the code and save again.
    })
  }

  return (
    <div className='m-4'>
      <AvForm
        onValidSubmit={(e, values) => {
          onUpdateLocker(values)
        }}
        onInvalidSubmit={() => {
          setLoading(false)
        }}>
        <div className='mb-3'>
          <label className='subtitle1 font-weight-bold'>Code</label>
          <Input
            type='text'
            name='code_display'
            inputMode='numeric'
            autoComplete='off'
            value={code}
            invalid={!!codeError}
            onChange={(e) => onCodeChange(e.target.value)}
            onBlur={() => {
              if (code) setCodeError(validateLockerCode(code))
            }}
            style={codeError ? { borderColor: '#dc3545', backgroundColor: '#fff5f5' } : undefined}
          />
          {codeError ? (
            <div className='text-danger subtitle1 mt-1' role='alert'>
              {codeError}
            </div>
          ) : (
            <div className='text-muted subtitle1 mt-1'>Must be exactly 4 digits. For Block Codes, the code must exist in this block group list.</div>
          )}
        </div>

        <RenderFields
          allForm={[formWithoutCode]}
          content={[formWithoutCode]}
          twoCol={true}
          defaultFormValue={{ ...defaultValue, code: digitsOnly(code).slice(0, 4) }}
          formFieldValue={formFieldValue}
          setFormFieldValue={setFormFieldValue}
          disabled_field={['domain_id', 'location_id', 'name', 'code']}
        />
        <div className='d-flex justify-content-end mt-5'>
          <button
            className='btn rounded-pill mt-2 text-danger border-danger mr-3'
            type='button'
            onClick={(e) => {
              onCloseForm()
              e.preventDefault()
              e.stopPropagation()
            }}
            style={{ fontWeight: 700, width: '12rem' }}>
            Cancel
          </button>
          <button
            className='btn text-white rounded-pill mt-2'
            disabled={!!loading}
            type='submit'
            style={{ background: primary_color, fontWeight: 700, width: '14rem' }}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </AvForm>
    </div>
  )
}

export default AddEditForm
